<template>
  <v-dialog :value="true" max-width="450px" :hide-overlay="true" :persistent="true">
    <v-card>
      <v-card-title class="headline">Validation</v-card-title>
      <v-card-text>
        <span v-if="$account.current.verification_method === 'SMS'">
          {{ $t('sms-code-text-1') }} <code>{{ $account.current.phone }}</code>. {{ $t('sms-code-text-2') }}
        </span>
        <span v-else>
          {{ $t('email-code-text-1') }} <code>{{ $account.current.email }}</code>. {{ $t('email-code-text-2') }}
        </span>

        <v-form v-model="forms.validate.valid" class="mt-4 mb-4 ma-auto position-relative" style="max-width: 300px">
          <v-otp-input
              length="4"
              v-model="forms.validate.fields.code"
              type="number"
              :disabled="forms.validate.loading"
              @finish="submit"
          ></v-otp-input>
        </v-form>

        <a @click="forms.resend.visible = !forms.resend.visible">{{ $t('code-not-received') }}</a>
        <v-card class="mt-4" v-show="forms.resend.visible">
          <v-card-text>
            <div v-if="$account.current.verification_method === 'SMS'">
              <v-form v-model="forms.resend.sms.valid">
                <vue-tel-input-vuetify
                    light
                    @keydown.enter.prevent="updatePhone"
                    @input="onPhoneInput"
                    single-line
                    :label="$t('label.phone') + '*'"
                    mode="international"
                    :preferredCountries="['US', 'FR', 'ES']"
                    v-model="forms.resend.sms.fields.phone"
                    :rules="forms.resend.sms.rules.phone">
                </vue-tel-input-vuetify>

                <div class="text-center">
                  <v-btn
                      class="mt-0 mt-md-4"
                      color="primary"
                      :disabled="forms.resend.timer > 0 || !forms.resend.sms.valid"
                      :loading="forms.resend.loading"
                      @click="updatePhone">
                    {{ forms.resend.timer > 0 ? $t('wait') + ' ' + forms.resend.timer + 's' : $t('resend-code') }}
                  </v-btn>
                </div>
              </v-form>
            </div>
            <div v-else>
              <v-form v-model="forms.resend.email.valid">
                <v-text-field
                    @keydown.enter.prevent="updateEmail"
                    :label="$t('label.email') + '*'"
                    v-model="forms.resend.email.fields.email"
                    :rules="forms.resend.email.rules.email"
                    required>
                </v-text-field>

                <div class="text-center">
                  <v-btn
                      class="mt-0 mt-md-4"
                      color="primary"
                      :disabled="forms.resend.timer > 0 || !forms.resend.email.valid"
                      :loading="forms.resend.loading"
                      @click="updateEmail">
                    {{ forms.resend.timer > 0 ? $t('wait') + ' ' + forms.resend.timer + 's' : $t('resend-code') }}
                  </v-btn>
                </div>
              </v-form>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions>
        <v-btn color="grey" text @click="cancel">
          {{ $t('actions.cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
            color="blue darken-1"
            :disabled="!forms.validate.valid"
            :loading="forms.validate.loading"
            text
            @click="submit">
          {{ $t('activate-account') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AVerifyAccountWidget',
  data () {
    return {
      forms: {
        validate: {
          valid: false,
          loading: false,
          fields: {
            code: ''
          },
          rules: {
            code: [
              v => !!v || this.$t('rules.code_required'),
              v => v.length >= 4 || this.$t('rules.code_required')
            ],
          },
        },
        resend: {
          visible: false,
          loading: false,
          timer: 60,
          sms: {
            valid: false,
            fields: {
              phone: '',
              phone_country: '',
            },
            validate: {
              phone: false
            },
            rules: {
              phone: [
                () => !!this.forms.resend.sms.validate.phone || this.$t('rules.phone_required'),
              ]
            },
          },
          email: {
            valid: false,
            fields: {
              email: ''
            },
            rules: {
              email: [
                v => !!v || this.$t('rules.email_required'),
                v => /.+@.+/.test(v) || this.$t('rules.valid_email_required'),
              ],
            },
          }
        }
      }
    }
  },
  methods: {
    async submit () {
      this.forms.validate.loading = true

      try {
        await this.$recaptchaLoaded()
        await this.$api.post('account/verify', {
          code: this.forms.validate.fields.code,
          _passive_client_token: await this.$recaptcha('password_reset')
        })

        await this.$account.fetch()
        this.$ui.success(this.$t('verified-success'))
        this.$emit('verified')
      } catch (e) {
        this.$ui.error(this.$t('invalid-code'))
      } finally {
        this.forms.validate.loading = false
      }
    },
    cancel () {
      this.$account.logout()
      this.$emit('cancel')
    },
    onPhoneInput (number, { valid, regionCode }) {
      this.forms.resend.sms.validate.phone = valid
      this.forms.resend.sms.fields.phone_country = regionCode.toLowerCase()
    },
    async updatePhone () {
      this.forms.resend.loading = true
      try {
        await this.$recaptchaLoaded()
        if(this.forms.resend.sms.fields.phone !== this.$account.current.phone) {
          await this.$api.put('account', {
            _passive_client_token: await this.$recaptcha('password_reset'),
            ...this.forms.resend.sms.fields
          })
        }
        await this.$api.post('account/verify/resend', {
          _passive_client_token: await this.$recaptcha('password_reset')
        })
        this.$ui.success(this.$t('new-code-sent'))
        this.forms.resend.timer = 60
      } finally {
        this.forms.resend.loading = false
      }
    },
    async updateEmail () {
      this.forms.resend.loading = true
      try {
        await this.$recaptchaLoaded()
        if(this.forms.resend.email.fields.email !== this.$account.current.email) {
          await this.$api.put('account', {
            _passive_client_token: await this.$recaptcha('password_reset'),
            ...this.forms.resend.email.fields
          })
        }
        await this.$api.post('account/verify/resend', {
          _passive_client_token: await this.$recaptcha('password_reset')
        })
        this.$ui.success(this.$t('new-code-sent'))
        this.forms.resend.timer = 60
      } finally {
        this.forms.resend.loading = false
      }
    }
  },
  mounted () {
    if (this.$account.isVerified) {
      this.$emit('verified')
    }

    this.forms.resend.sms.fields.phone = this.$account.current.phone
    this.forms.resend.email.fields.email = this.$account.current.email
  },
  created () {
    setInterval(() => {
      if (this.forms.resend.timer > 0) {
        this.forms.resend.timer = this.forms.resend.timer - 1
      }
    }, 1000)
  }
}
</script>

<i18n>
{
	"en": {
		"activate-account": "Activate",
		"code-not-received": "Haven't received a code?",
		"email-code-text-1": "An email containing a validation code has been sent to you on",
		"email-code-text-2": "Enter the code below to complete your registration.",
		"invalid-code": "Your validation code is invalid or expired.",
		"label": {
			"email": "E-mail",
			"phone": "Phone"
		},
		"label-code": "Validation code",
		"new-code-sent": "A new validation code has been sent to you.",
		"resend-code": "Send new code",
		"rules": {
			"code_required": "A code is required.",
			"email_required": "Your email is required.",
			"phone_required": "Your phone is required.",
			"valid_email_required": "Your email must be valid."
		},
		"sms-code-text-1": "An SMS containing a validation code has been sent to you on",
		"sms-code-text-2": "Enter the code below to complete your registration.",
		"verified-success": "Your account has been validated successfully!",
		"wait": "wait"
	},
	"es": {
		"activate-account": "Activar",
		"code-not-received": "¿No has recibido un código?",
		"email-code-text-1": "Se le ha enviado un email con un código de validación el",
		"email-code-text-2": "Ingrese el código a continuación para completar su registro.",
		"invalid-code": "Su código de validación no es válido o ha caducado.",
		"label": {
			"email": "E-mail",
			"phone": "Teléfono"
		},
		"label-code": "Código de Validación",
		"new-code-sent": "Se le ha enviado un nuevo código de validación.",
		"resend-code": "Enviar nuevo código",
		"rules": {
			"code_required": "Se requiere un código.",
			"email_required": "Su correo electrónico es requerido.",
			"phone_required": "Se requiere su teléfono.",
			"valid_email_required": "Su correo electrónico debe ser válido."
		},
		"sms-code-text-1": "Se le ha enviado un SMS con un código de validación el",
		"sms-code-text-2": "Ingrese el código a continuación para completar su registro.",
		"verified-success": "¡Su cuenta ha sido validada con éxito!",
		"wait": "Esperar"
	},
	"fr": {
		"activate-account": "Activer",
		"code-not-received": "Vous n'avez pas reçu de code ?",
		"email-code-text-1": "Un email contenant un code de validation vous a été envoyé sur",
		"email-code-text-2": "Saisissez le code ci-dessous afin de compléter votre inscription.",
		"invalid-code": "Votre code de validation est invalide ou expiré.",
		"label": {
			"email": "Email",
			"phone": "Téléphone"
		},
		"label-code": "Code de validation",
		"new-code-sent": "Un nouveau code de validation vous a été envoyé.",
		"resend-code": "Envoyer nouveau code",
		"rules": {
			"code_required": "Un code est requis.",
			"email_required": "Votre email est requis.",
			"phone_required": "Votre téléphone est requis.",
			"valid_email_required": "Votre email doit être valide."
		},
		"sms-code-text-1": "Un SMS contenant un code de validation vous a été envoyé sur",
		"sms-code-text-2": "Saisissez le code ci-dessous afin de compléter votre inscription.",
		"verified-success": "Votre compte a été validé avec succès !",
		"wait": "Patientez"
	},
	"it": {
		"activate-account": "Attiva",
		"code-not-received": "Non hai ricevuto un codice?",
		"email-code-text-1": "Un'email contenente un codice di convalida ti è stata inviata il",
		"email-code-text-2": "Inserisci il codice qui sotto per completare la tua registrazione.",
		"invalid-code": "Il tuo codice di convalida non è valido o è scaduto.",
		"label": {
			"email": "Email",
			"phone": "Telefono"
		},
		"label-code": "Codice di convalida",
		"new-code-sent": "Un nuovo codice di convalida ti è stato inviato.",
		"resend-code": "Invia nuovo codice",
		"rules": {
			"code_required": "È richiesto un codice.",
			"email_required": "La tua email è obbligatoria.",
			"phone_required": "Il tuo telefono è obbligatorio.",
			"valid_email_required": "La tua email deve essere valida."
		},
		"sms-code-text-1": "Un SMS contenente un codice di convalida ti è stato inviato il",
		"sms-code-text-2": "Inserisci il codice qui sotto per completare la tua registrazione.",
		"verified-success": "Il tuo account è stato convalidato con successo!",
		"wait": "attendi"
	},
	"pt": {
		"activate-account": "Ativar",
		"code-not-received": "Não recebeu um código?",
		"email-code-text-1": "Foi-lhe enviado um email com um código de validação no",
		"email-code-text-2": "Introduza o código abaixo para concluir o seu registo.",
		"invalid-code": "O seu código de validação é inválido ou expirou.",
		"label": {
			"email": "Email",
			"phone": "Telefone"
		},
		"label-code": "Código de validação",
		"new-code-sent": "Foi-lhe enviado um novo código de validação.",
		"resend-code": "Enviar novo código",
		"rules": {
			"code_required": "É necessário um código.",
			"email_required": "O seu email é obrigatório.",
			"phone_required": "O seu telefone é obrigatório.",
			"valid_email_required": "O seu email deve ser válido."
		},
		"sms-code-text-1": "Foi-lhe enviado um SMS com um código de validação no",
		"sms-code-text-2": "Introduza o código abaixo para concluir o seu registo.",
		"verified-success": "A sua conta foi validada com sucesso!",
		"wait": "aguarde"
	},
	"pl": {
		"activate-account": "Aktywuj",
		"code-not-received": "Nie otrzymałeś kodu?",
		"email-code-text-1": "Na Twój adres email został wysłany email zawierający kod weryfikacyjny",
		"email-code-text-2": "Wprowadź poniższy kod, aby zakończyć rejestrację.",
		"invalid-code": "Twój kod weryfikacyjny jest nieprawidłowy lub wygasł.",
		"label": {
			"email": "E-mail",
			"phone": "Telefon"
		},
		"label-code": "Kod weryfikacyjny",
		"new-code-sent": "Nowy kod weryfikacyjny został do Ciebie wysłany.",
		"resend-code": "Wyślij nowy kod",
		"rules": {
			"code_required": "Kod jest wymagany.",
			"email_required": "Twój email jest wymagany.",
			"phone_required": "Twój telefon jest wymagany.",
			"valid_email_required": "Twój email musi być prawidłowy."
		},
		"sms-code-text-1": "Na Twój numer telefonu został wysłany SMS zawierający kod weryfikacyjny",
		"sms-code-text-2": "Wprowadź poniższy kod, aby zakończyć rejestrację.",
		"verified-success": "Twoje konto zostało pomyślnie zweryfikowane!",
		"wait": "poczekaj"
	}
}
</i18n>
